import z from 'zod';
import type { AssignedWorkflowScheduledEventDto } from './assignedWorkflow.dto';
import type { AddressDto } from './interactions/interactable.dto';
import type { UserDto } from './user.dto';

export type EventTypeDto = 'call' | 'message' | 'visit' | 'tbd' | 'reminder';

export interface ScheduledEventViewDto {
  scheduledEvents: ScheduledEventDto[];
  members: UserDto[];
}

export interface ScheduledEventDto {
  id: number;
  userId: string;
  date: string;
  time: number;
  type: EventTypeDto;
  note: string;
  createdBy: string;
  originalDate: string;
  originalTime: number;
  snoozeCount: number;
  resolved: boolean;
  estimatedDuration: number;
  visitLocation: string | AddressDto;
  assignedWorkflowScheduledEvents: AssignedWorkflowScheduledEventDto[];
}

export interface CreateScheduledEventRequestDto {
  date?: string;
  time?: number;
  type: EventTypeDto;
  note?: string;
  visitLocationAddressId?: number;
  visitLocation?: string;
  assignedWorkflowPhaseIds?: number[];
  suggestedScheduledEventId?: string;
}

export type UpdateScheduledEventRequestDto = CreateScheduledEventRequestDto;

export const ScheduledEventFormSchema = z
  .object({
    date: z
      .string()
      .min(1, 'Date is required')
      .refine(
        val => {
          // Validate the date is in the correct format (yyyy-MM-dd)
          const regex = /^\d{4}-\d{2}-\d{2}$/;
          if (!regex.test(val)) return false;
          // Validate it's actually a valid date
          const date = new Date(val + 'T00:00:00');
          return !isNaN(date.getTime()) && date.toISOString().startsWith(val);
        },
        {
          message: 'Date must be a valid date in the format YYYY-MM-DD',
        }
      ),
    time: z.number({
      required_error: 'Time is required',
    }),
    eventType: z.enum(['call', 'message', 'visit', 'tbd', 'reminder'], {
      required_error: 'Event type is required',
    }),
    visitLocationId: z.number().optional().nullable(),
    otherLocation: z.string().nullable().optional(),
    note: z.string().optional(),
    suggestedScheduledEventId: z.string().optional(),
  })
  .refine(
    data => {
      // If event type is 'visit', then either visitLocationId or otherLocation must be provided
      if (data.eventType === 'visit') {
        return (
          (data.visitLocationId !== undefined && data.visitLocationId !== -1) ||
          (data.otherLocation !== undefined &&
            data.otherLocation !== null &&
            data.otherLocation.trim() !== '')
        );
      }
      return true;
    },
    {
      message: 'Location is required for visit events',
      path: ['otherLocation'],
    }
  );

export type ScheduledEventFormType = z.infer<typeof ScheduledEventFormSchema>;
